"use client";
import { Track } from "@/src/lib/tracking";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { OspModal, ospThemes } from "@ospweb/sdk-ui";
import { useIsPc } from "@hooks/useIsPc";

export interface CandleViewModalProps {
  className?: string;
  showModal: boolean;
  onClose: () => void;
  symbol?: string;
  title?: string;
  /** 市场大写:当前币种USDT */
  market_symbol?: string;
  widgetPropsOptions?: any;
}

const DEFAULT_CONFIG = {
  // fullscreen: true,
  symbol: "BINANCE:BTCUSDT",
  interval: "1D",
  timezone: "Etc/UTC",
  theme: "dark",
  style: "1",
  locale: "en",
  enable_publishing: false,
  hide_side_toolbar: true,
  allow_symbol_change: false,
  loading_screen: {
    backgroundColor: ospThemes.colors.background_solid_secondary,
  },
  container_id: "tradingview_chart",
  save_image: false,
  // studies: ["RSI@tv-basicstudies"],
  control_bar: false,
  show_popup_button: false,
  hideideas: true,
  // popup_width: '1000',
  // popup_height: '650',
};

const CandleViewModal: React.FC<CandleViewModalProps> = ({
  showModal,
  onClose,
  symbol,
  className,
  title,
  market_symbol,
  widgetPropsOptions,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const chartRef = useRef<any>(null);
  const retryCountRef = useRef<number>(0);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  const [isPC] = useIsPc();
  // 计算实际使用的交易对
  const getSymbol = useCallback(() => {
    if (market_symbol) {
      return market_symbol;
    }
    if (symbol) {
      return `BINANCE:${symbol}USDT`;
    }
    return DEFAULT_CONFIG.symbol;
  }, [symbol, market_symbol]);

  // 初始化TradingView小部件
  const initWidget = useCallback(() => {
    if (!containerRef.current || !window.TradingView) {
      Track.log(
        "warn",
        "CandleViewModal",
        "TradingView not loaded or container not ready",
      );
      return false;
    }

    try {
      // 确保容器清空
      containerRef.current.innerHTML = "";

      // 创建唯一ID
      const containerId = `tradingview_${Math.random().toString(36).substring(2, 8)}`;
      containerRef.current.id = containerId;

      const h5Props = isPC
        ? {}
        : {
            hide_side_toolbar: !isPC,
            enabled_features: ["hide_left_toolbar"],
            disabled_features: ["use_localstorage_for_settings"],
          };

      // 配置小部件
      const widgetOptions = {
        ...DEFAULT_CONFIG,
        symbol: getSymbol(),
        container_id: containerId,
        backgroundColor: ospThemes.colors.background_solid_secondary,
        ...widgetPropsOptions,
        ...h5Props,
      };

      // 创建小部件
      chartRef.current = new window.TradingView.widget(widgetOptions);
      return true;
    } catch (error) {
      Track.log(
        "error",
        "CandleViewModal",
        "Failed to initialize TradingView widget:",
        { cause: String(error) },
      );
      return false;
    }
  }, [getSymbol, isPC]);

  // 加载TradingView脚本
  const loadTradingViewScript = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      // 如果已经加载过脚本且成功，直接返回
      if (scriptLoaded && window.TradingView) {
        resolve(true);
        return;
      }

      // 如果超过最大尝试次数(5次)，不再尝试加载
      if (retryCountRef.current >= 5) {
        Track.log(
          "error",
          "CandleViewModal",
          "Exceeded maximum retry attempts (5) for TradingView script",
        );
        resolve(false);
        return;
      }

      // 如果有加载错误，重置状态重新尝试
      if (scriptError) {
        setScriptError(false);
      }

      // 如果已经有脚本引用但没有加载成功，移除旧脚本
      if (scriptRef.current) {
        try {
          document.head.removeChild(scriptRef.current);
          scriptRef.current = null;
        } catch (e) {
          Track.log("warn", "CandleViewModal", "Failed to remove old script", {
            cause: String(e),
          });
        }
      }

      // 创建并加载新脚本
      scriptRef.current = document.createElement("script");
      scriptRef.current.src = "https://s3.tradingview.com/tv.js";
      scriptRef.current.async = true;

      // 增加重试次数
      retryCountRef.current += 1;
      const currentRetryCount = retryCountRef.current;

      // 成功加载
      scriptRef.current.onload = () => {
        setScriptLoaded(true);
        resolve(true);
      };

      // 加载失败
      scriptRef.current.onerror = () => {
        setScriptError(true);
        Track.log(
          "error",
          "CandleViewModal",
          `Failed to load TradingView script (attempt ${currentRetryCount}/5)`,
        );
        resolve(false);
      };

      document.head.appendChild(scriptRef.current);
    });
  }, [scriptLoaded, scriptError]);

  // 当模态框显示时初始化图表
  useEffect(() => {
    // 异步加载脚本并初始化组件
    const initializeChart = async () => {
      // 加载脚本
      const scriptLoaded = await loadTradingViewScript();

      // 如果脚本加载成功，等待一小段时间确保TradingView对象完全初始化
      if (scriptLoaded) {
        // 延迟初始化以确保TradingView对象已完全加载
        setTimeout(() => {
          if (window.TradingView) {
            initWidget();
          } else {
            Track.log(
              "warn",
              "CandleViewModal",
              "TradingView object not available after script load",
            );
            // 如果TradingView对象仍不可用，再尝试一次
            setTimeout(() => {
              if (window.TradingView) {
                initWidget();
              }
            }, 500);
          }
        }, 100);
      }
    };

    initializeChart();

    return () => {
      // 清理小部件和事件监听器
      if (chartRef.current) {
        try {
          // 尝试销毁图表实例（如果有销毁方法）
          if (chartRef.current.remove) {
            chartRef.current.remove();
          }
          chartRef.current = null;
        } catch (error) {
          Track.log(
            "error",
            "CandleViewModal",
            "Error cleaning up TradingView widget:",
            { cause: String(error) },
          );
        }
      }
    };
  }, [showModal, loadTradingViewScript, initWidget]);

  return (
    <OspModal
      open={showModal}
      onOk={onClose}
      onCancel={onClose}
      onClose={onClose}
      footer={null}
      title={
        typeof title === "string" ? (
          <div className="headline_regular_m text-content_primary mb-4 md:mb-6 ">
            {title}
          </div>
        ) : (
          title
        )
      }
      closeIconSize={24}
      contentStyle={!isPC ? { padding: 16 } : {}}
      // contentStyle={{
      //   minHeight: isPC ? "calc(100vh - 64px)" : "calc(100vh - 16px)",
      //   height: isPC ? "auto" : "calc(100vh - 16px)",
      //   overflow: "hidden",
      //   width: isPC ? "calc(100vw - 80px)" : "calc(100vw - 16px)",
      // }}
      containerClassName="w-full w-full"
    >
      <div
        className={`
          tradingview-container
          [&>div:first-child]:md:!w-[calc(100vw-160px)]
          [&>div:first-child]:!w-[calc(100vw-64px)]
          [&>div:first-child]:md:!h-[calc(100vh-212px)]
          [&>*]:!rounded-[32px]
          ${className || ""}`}
        ref={containerRef}
      ></div>
    </OspModal>
  );
};

export default CandleViewModal;
